import { Controller } from "@hotwired/stimulus"

// Enhances server-rendered shells with native scroll-snap and one bounded
// previous/current/next frame window. Turbo owns frame requests/rendering;
// selection persistence is a separate controller listening for the settled event.
export default class extends Controller {
  static targets = [ "track", "status" ]
  static values = {
    updateDocumentTitle: Boolean,
  }

  connect() {
    this.slides = Array.from(this.trackTarget.querySelectorAll(":scope > .ve-ds"))
    this.frames = this.slides.map((slide) => slide.querySelector(":scope > turbo-frame"))
    this.activeIndex = this.initialIndex()
    this.suppressClickUntil = 0
    this.suspended = false
    this.pointerActive = false
    this.touchActive = false
    this.scrollEndSupported = "onscrollend" in this.trackTarget
    this.scrollSinceInteraction = false
    this.swallowedScrollEnd = false
    this.framesLoadedWhileInert = new WeakSet()

    this.trackTarget.dataset.sliderReady = "true"
    this.trackTarget.scrollLeft = this.offsetFor(this.activeIndex)
    this.activate(this.activeIndex)
    this.markSettled(this.activeIndex)
    this.observeFrames()
  }

  disconnect() {
    window.clearTimeout(this.scrollTimer)
    this.frameObserver?.disconnect()
  }

  beforeCache() {
    this.suspended = true
    window.clearTimeout(this.scrollTimer)
    this.trackTarget.dataset.sliderReady = "false"
    this.trackTarget.scrollLeft = 0
  }

  resume(event) {
    if (event.type === "visibilitychange" && document.visibilityState === "hidden") return

    this.suspended = false
    this.trackTarget.dataset.sliderReady = "true"
    this.trackTarget.scrollLeft = this.offsetFor(this.activeIndex)
    this.activate(this.activeIndex)
    this.observeFrames()
  }

  scroll() {
    if (this.suspended) return

    this.element.dataset.sliderInteracting = ""
    this.scrollSinceInteraction = true
    this.scheduleFallbackSettle()
  }

  scrollEnd() {
    if (this.suspended) return
    if (this.pointerActive || this.touchActive) {
      this.swallowedScrollEnd = true
      return
    }

    window.clearTimeout(this.scrollTimer)
    this.settle()
  }

  pointerDown() {
    if (!this.pointerActive && !this.touchActive) this.beginInteraction()
    this.pointerActive = true
    this.pointerStartLeft = this.trackTarget.scrollLeft
  }

  pointerUp() {
    if (Math.abs(this.trackTarget.scrollLeft - this.pointerStartLeft) > 8) {
      this.suppressClickUntil = Date.now() + 350
    }
    this.pointerActive = false
    this.scheduleFallbackSettle()
  }

  pointerCancel() {
    this.pointerActive = false
    this.scheduleFallbackSettle()
  }

  touchStart() {
    if (!this.pointerActive && !this.touchActive) this.beginInteraction()
    this.touchActive = true
  }

  touchEnd(event) {
    this.touchActive = (event.touches?.length || 0) > 0
    this.scheduleFallbackSettle()
  }

  touchCancel(event) {
    this.touchActive = (event.touches?.length || 0) > 0
    this.scheduleFallbackSettle()
  }

  click(event) {
    const control = event.target.closest("[data-slider-direction]")
    if (control) return this.navigate(event, Number(control.dataset.sliderDirection))

    if (Date.now() < this.suppressClickUntil && event.target.closest(".ve-uslide-card")) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  frameLoad(event) {
    const frame = event.target
    if (!this.frames.includes(frame)) return

    if (frame.parentElement?.hasAttribute("inert")) this.framesLoadedWhileInert.add(frame)
    frame.dataset.loaded = "true"
    delete frame.dataset.error
  }

  navigate(event, direction) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const destination = this.activeIndex + direction
    if (!this.slides[destination]) return

    event.preventDefault()
    this.trackTarget.scrollTo({
      left: this.offsetFor(destination),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    })
  }

  settle() {
    delete this.element.dataset.sliderInteracting
    this.scrollSinceInteraction = false
    this.swallowedScrollEnd = false
    if (this.suspended || !this.element.isConnected || this.slides.length === 0) return

    const index = this.nearestIndex()
    if (!Number.isInteger(index) || !this.slides[index]) return

    const departedIndex = this.activeIndex
    const changed = index !== departedIndex
    const previouslySettled = this.settled(index)
    const frame = this.frames[index]
    const loadedWhileInert = this.framesLoadedWhileInert.has(frame)
    this.activate(index)
    if (changed) {
      this.markSettled(index)
      this.framesLoadedWhileInert.delete(frame)
      this.dispatch("settled", {
        detail: {
          departedIndex,
          key: this.slides[index].dataset.k,
          revalidationEligible: previouslySettled && frame?.dataset.loaded === "true" && !loadedWhileInert,
        },
      })
    }
  }

  beginInteraction() {
    window.clearTimeout(this.scrollTimer)
    this.scrollSinceInteraction = false
    this.swallowedScrollEnd = false
    this.element.dataset.sliderInteracting = ""
  }

  scheduleFallbackSettle() {
    window.clearTimeout(this.scrollTimer)
    if (this.pointerActive || this.touchActive) return
    if (this.scrollEndSupported && this.scrollSinceInteraction && !this.swallowedScrollEnd) return

    this.scrollTimer = window.setTimeout(() => this.settle(), 120)
  }

  activate(index) {
    const active = this.slides[index]
    if (!active) return

    this.activeIndex = index
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index
      slide.dataset.active = String(active)
      slide.toggleAttribute("inert", !active)
      if (active) slide.removeAttribute("aria-hidden")
      else slide.setAttribute("aria-hidden", "true")
    })
    this.prewarmFrames(index)

    const name = active.querySelector(".ve-fleet-badge-name")?.textContent.trim()
    const city = active.querySelector(".ve-fleet-badge-city")?.textContent.trim()
    const identity = [ name, city ].filter(Boolean).join(" ") || "Slide"
    if (this.hasStatusTarget) {
      const announcement = `${identity}, ${index + 1} of ${this.slides.length}`
      this.statusTarget.setAttribute("aria-label", announcement)
      this.statusTarget.textContent = announcement
    }
    if (this.updateDocumentTitleValue) document.title = `${identity} · Machine`
  }

  observeFrames() {
    this.frameObserver?.disconnect()
    this.frameObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio <= 0) return
        const index = this.slides.indexOf(entry.target)
        this.loadFrame(this.frames[index])
        this.frameObserver.unobserve(entry.target)
      })
    }, { root: this.trackTarget, threshold: [ 0, 0.01 ] })

    this.slides.forEach((slide, index) => {
      if (this.frames[index]?.dataset.loaded !== "true") this.frameObserver.observe(slide)
    })
  }

  prewarmFrames(index) {
    [ index - 1, index, index + 1 ].forEach((frameIndex) => this.loadFrame(this.frames[frameIndex]))
  }

  loadFrame(frame) {
    if (!frame || frame.dataset.loaded === "true" || frame.hasAttribute("src")) return
    if (frame.dataset.src) {
      frame.disabled = true
      frame.refresh = "morph"
      frame.src = frame.dataset.src
      frame.reload()
      frame.loading = "eager"
      frame.disabled = false
    }
  }

  initialIndex() {
    const active = this.slides.findIndex((slide) => !slide.hasAttribute("inert"))
    return active >= 0 ? active : 0
  }

  nearestIndex() {
    const gap = parseFloat(getComputedStyle(this.trackTarget).columnGap) || 0
    const width = this.trackTarget.clientWidth + gap
    if (width <= 0) return this.activeIndex

    return Math.max(0, Math.min(this.slides.length - 1, Math.round(this.trackTarget.scrollLeft / width)))
  }

  offsetFor(index) {
    return this.slides[index]?.offsetLeft - this.trackTarget.offsetLeft || 0
  }

  settled(index) {
    return this.slides[index]?.dataset.settled === "true"
  }

  markSettled(index) {
    if (this.slides[index]) this.slides[index].dataset.settled = "true"
  }
}
